// src/components/visador/VisadorPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  CheckCircleOutline,
  Refresh,
  Close,
  Save,
  AssignmentTurnedIn,
  ArrowDropDown,
  PictureAsPdf,
  AttachFile as AttachIcon,
} from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';

import { asociacionesService } from '../../services/asociacionesService';
import { catalogosService } from '../../services/catalogosService';
import { dtedocService } from '../../services/dtedocService';
import { generarPeriodos } from '../../utils/periodos';
import { formatearRut, formatearMonto, formatearFecha, getTipoDocLabel } from '../../utils/formatters';
import { setEmpresaActiva } from '../../redux/empresaSlice';

// ─── helpers ────────────────────────────────────────────────────────────────

const periodoActual = () => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = String(hoy.getMonth() + 1).padStart(2, '0');
  return `${y}${m}`;
};

// ─── MovimientoCard ──────────────────────────────────────────────────────────

const MovimientoCard = ({ mov, onClick, tieneAdjunto }) => (
  <Card
    elevation={2}
    sx={{
      mb: 1.5,
      borderRadius: 2,
      overflow: 'hidden',
      '&:active': { transform: 'scale(0.99)', transition: 'transform 0.1s' },
    }}
  >
    <CardActionArea onClick={() => onClick(mov)} sx={{ p: 0 }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Proveedor */}
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: 0.25 }}
          noWrap
        >
          {mov.razon_social || formatearRut(mov.rut_proveedor, mov.dv_proveedor)}
        </Typography>
        {/* RUT + tipo doc */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            {formatearRut(mov.rut_proveedor, mov.dv_proveedor)}
          </Typography>
          <Chip
            label={getTipoDocLabel(mov.tipo_doc)}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>

        {/* Folio + fecha + adjunto + monto */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            F.{mov.folio} · {formatearFecha(mov.fecha_docto)}
          </Typography>
          {tieneAdjunto && (
            <AttachIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
          )}
          <Typography fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, whiteSpace: 'nowrap', flexShrink: 0, pl: 0.5 }}>
            {formatearMonto(mov.monto_total)}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

// ─── MovimientoCardSkeleton ──────────────────────────────────────────────────

const MovimientoCardSkeleton = () => (
  <Card elevation={1} sx={{ mb: 1.5, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Skeleton variant="text" width="60%" height={22} />
      <Skeleton variant="text" width="35%" height={16} sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Skeleton variant="rounded" width={120} height={22} />
        <Skeleton variant="text" width={80} height={22} />
      </Box>
    </CardContent>
  </Card>
);

// ─── AsociarDialog ───────────────────────────────────────────────────────────

const AsociarDialog = ({
  open,
  movimiento,
  rutEmpresa,
  periodo,
  cuentas,
  centros,
  clasificadores,
  onClose,
  onGuardado,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
  const [centroSeleccionado, setCentroSeleccionado] = useState('');
  const [clasificadorSeleccionado, setClasificadorSeleccionado] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [cargandoSugerencia, setCargandoSugerencia] = useState(false);

  // PDF adjunto
  const [adjuntoId, setAdjuntoId] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Pre-fill from suggestion when dialog opens
  useEffect(() => {
    if (!open || !movimiento || !rutEmpresa) return;

    setCuentaSeleccionada(null);
    setCentroSeleccionado('');
    setClasificadorSeleccionado('');
    setError('');
    setAdjuntoId(null);
    setPdfOpen(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    setCargandoSugerencia(true);

    // Check adjunto: obtener documento asociado al movimiento
    dtedocService.getDocumentosByMovimiento(movimiento.id)
      .then(docs => { if (docs?.length) setAdjuntoId(docs[0].id); })
      .catch(() => {});

    asociacionesService
      .getSugerencia(rutEmpresa, movimiento.rut_proveedor, movimiento.folio)
      .then((sug) => {
        if (sug) {
          if (sug.cuenta_id) {
            const found = cuentas.find((c) => c.id === sug.cuenta_id);
            if (found) setCuentaSeleccionada(found);
          }
          if (sug.centro_id) {
            setCentroSeleccionado(sug.centro_id.toString());
          }
          if (sug.clasificador_id) {
            setClasificadorSeleccionado(sug.clasificador_id.toString());
          }
        }
      })
      .catch(() => {
        // silently ignore — no suggestions available
      })
      .finally(() => setCargandoSugerencia(false));
  }, [open, movimiento, rutEmpresa, cuentas]);

  const handleVerPdf = async () => {
    if (pdfUrl) { setPdfOpen(true); return; }
    setLoadingPdf(true);
    try {
      const blob = await dtedocService.getDocumentoPdf(adjuntoId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfOpen(true);
    } catch (err) {
      console.error('Error al cargar PDF:', err);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleGuardar = async () => {
    if (!cuentaSeleccionada) {
      setError('Debe seleccionar una cuenta contable');
      return;
    }

    setGuardando(true);
    setError('');

    try {
      const datos = {
        rut_empresa: rutEmpresa,
        periodo: periodo,
        rut_proveedor: movimiento.rut_proveedor,
        folio: movimiento.folio,
        cuenta_id: cuentaSeleccionada.id,
        centro_id: centroSeleccionado ? parseInt(centroSeleccionado) : null,
        clasificador_id: clasificadorSeleccionado ? parseInt(clasificadorSeleccionado) : null,
        entidad_id: null,
      };

      await asociacionesService.crearAsociacion(datos);
      onGuardado(movimiento);
    } catch (err) {
      setError(err.message || 'Error al guardar la asociación');
    } finally {
      setGuardando(false);
    }
  };

  if (!movimiento) return null;

  return (
    <>
    {/* PDF viewer dialog */}
    <Dialog
      open={pdfOpen}
      onClose={() => setPdfOpen(false)}
      fullScreen
      sx={{ zIndex: 1400 }}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <IconButton edge="start" onClick={() => setPdfOpen(false)} aria-label="cerrar PDF">
            <Close />
          </IconButton>
          <Typography variant="subtitle1" sx={{ ml: 1, flexGrow: 1 }} noWrap>
            PDF — F.{movimiento.folio} · {movimiento.razon_social || formatearRut(movimiento.rut_proveedor, movimiento.dv_proveedor)}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, height: 'calc(100vh - 48px)' }}>
        {pdfUrl && (
          <iframe
            src={`${pdfUrl}#view=FitH`}
            title="Adjunto PDF"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </Box>
    </Dialog>

    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          ...(isMobile && { m: 0 }),
        },
      }}
    >
      {/* Header — visible in both mobile and desktop */}
      {isMobile ? (
        <AppBar position="static" color="primary" elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              disabled={guardando}
              aria-label="cerrar"
            >
              <Close />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>
              Asociar documento
            </Typography>
            <Button
              color="inherit"
              onClick={handleGuardar}
              disabled={!cuentaSeleccionada || guardando}
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <Save />}
            >
              Guardar
            </Button>
          </Toolbar>
        </AppBar>
      ) : (
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Asociar documento</Typography>
            <IconButton onClick={onClose} disabled={guardando} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent sx={{ pt: 2, px: isMobile ? 2 : 3 }}>
        {/* Movement info */}
        <Box
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {movimiento.razon_social || formatearRut(movimiento.rut_proveedor, movimiento.dv_proveedor)}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            RUT: {formatearRut(movimiento.rut_proveedor, movimiento.dv_proveedor)}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Tipo
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {getTipoDocLabel(movimiento.tipo_doc)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Folio
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {movimiento.folio}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fecha
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatearFecha(movimiento.fecha_docto)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                {formatearMonto(movimiento.monto_total)}
              </Typography>
            </Box>
            {adjuntoId && (
              <Box sx={{ ml: 'auto' }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={loadingPdf ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdf />}
                  onClick={handleVerPdf}
                  disabled={loadingPdf}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Ver PDF
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Form fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Cuenta — Autocomplete */}
          <Autocomplete
            value={cuentaSeleccionada}
            onChange={(_, newVal) => setCuentaSeleccionada(newVal)}
            options={cuentas}
            getOptionLabel={(opt) => `${opt.codigo} - ${opt.nombre}`}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            loading={cargandoSugerencia}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cuenta Contable *"
                error={!!error && !cuentaSeleccionada}
                helperText={!cuentaSeleccionada && error ? error : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {cargandoSugerencia ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText="Sin resultados"
          />

          {/* Centro de Costo */}
          <FormControl fullWidth>
            <InputLabel id="visador-centro-label">Centro de Costo</InputLabel>
            <Select
              labelId="visador-centro-label"
              value={centroSeleccionado}
              label="Centro de Costo"
              onChange={(e) => setCentroSeleccionado(e.target.value)}
              disabled={guardando}
            >
              <MenuItem value="">
                <em>Sin centro</em>
              </MenuItem>
              {centros.map((c) => (
                <MenuItem key={c.id} value={c.id.toString()}>
                  {c.codigo} - {c.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Concepto / Clasificador */}
          <FormControl fullWidth>
            <InputLabel id="visador-clasificador-label">Concepto (Clasificador)</InputLabel>
            <Select
              labelId="visador-clasificador-label"
              value={clasificadorSeleccionado}
              label="Concepto (Clasificador)"
              onChange={(e) => setClasificadorSeleccionado(e.target.value)}
              disabled={guardando}
            >
              <MenuItem value="">
                <em>Sin clasificador</em>
              </MenuItem>
              {clasificadores.map((cl) => (
                <MenuItem key={cl.id} value={cl.id.toString()}>
                  {cl.codigo} - {cl.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* General error */}
          {error && cuentaSeleccionada && (
            <Alert severity="error" sx={{ mt: 0 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      {/* Desktop actions only — mobile uses AppBar button */}
      {!isMobile && (
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} disabled={guardando} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardar}
            disabled={!cuentaSeleccionada || guardando}
            startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <Save />}
          >
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
    </>
  );
};

// ─── VisadorPage ─────────────────────────────────────────────────────────────

const VisadorPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const { empresas, empresaActiva } = useSelector((state) => state.empresa);

  // Derive rutEmpresa from empresaActiva
  const rutEmpresa = empresaActiva?.rut || '';

  // Periods
  const [periodos] = useState(() => generarPeriodos());
  const [periodo, setPeriodo] = useState(() => periodoActual());

  // Movements
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const [adjuntosIds, setAdjuntosIds] = useState(new Set()); // movimiento ids con adjunto

  // Catalogs
  const [cuentas, setCuentas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [clasificadores, setClasificadores] = useState([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [movimientoActivo, setMovimientoActivo] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load catalogs when empresa changes ──────────────────────────────────────
  const cargarCatalogos = useCallback(async (rut) => {
    if (!rut) return;
    setCargandoCatalogos(true);
    try {
      const [c, ce, cl] = await Promise.all([
        catalogosService.getCuentas(true, rut),
        catalogosService.getCentros(true, rut),
        catalogosService.getClasificadores(null, true, rut),
      ]);
      setCuentas(c);
      setCentros(ce);
      setClasificadores(cl);
    } catch (err) {
      console.error('Error al cargar catálogos:', err);
    } finally {
      setCargandoCatalogos(false);
    }
  }, []);

  useEffect(() => {
    if (rutEmpresa) {
      cargarCatalogos(rutEmpresa);
    }
  }, [rutEmpresa, cargarCatalogos]);

  // ── Load pending movements ───────────────────────────────────────────────────
  const cargarPendientes = useCallback(async () => {
    if (!rutEmpresa || !periodo) return;
    setLoading(true);
    setErrorCarga('');
    try {
      const data = await asociacionesService.getDocumentosPendientes(rutEmpresa, periodo);
      const movs = Array.isArray(data) ? data : [];
      setMovimientos(movs);
      // Batch check adjuntos
      if (movs.length > 0) {
        dtedocService.getMovimientosConAdjunto(movs.map(m => m.id))
          .then(idsConAdjunto => setAdjuntosIds(new Set(idsConAdjunto)))
          .catch(() => {});
      } else {
        setAdjuntosIds(new Set());
      }
    } catch (err) {
      setErrorCarga(err.message || 'Error al cargar documentos pendientes');
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  }, [rutEmpresa, periodo]);

  useEffect(() => {
    cargarPendientes();
  }, [cargarPendientes]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleCardClick = (mov) => {
    setMovimientoActivo(mov);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setMovimientoActivo(null);
  };

  const handleGuardado = (movimientoAsociado) => {
    // Remove from list
    setMovimientos((prev) =>
      prev.filter(
        (m) =>
          !(
            m.rut_proveedor === movimientoAsociado.rut_proveedor &&
            m.folio === movimientoAsociado.folio
          )
      )
    );
    setDialogOpen(false);
    setMovimientoActivo(null);
    setSnackbar({
      open: true,
      message: 'Asociacion guardada correctamente',
      severity: 'success',
    });
  };

  const handleEmpresaChange = (e) => {
    const rut = e.target.value;
    const empresa = empresas.find((emp) => emp.rut === rut);
    if (empresa) {
      dispatch(setEmpresaActiva(empresa));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 600, width: '100%', mx: 'auto', py: 2 }}>

      {/* Controls row */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {/* Empresa selector */}
        <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel id="visador-empresa-label">Empresa</InputLabel>
          <Select
            labelId="visador-empresa-label"
            value={rutEmpresa}
            label="Empresa"
            onChange={handleEmpresaChange}
            IconComponent={ArrowDropDown}
          >
            {empresas.map((emp) => (
              <MenuItem key={emp.id} value={emp.rut}>
                {emp.nombre_corto || emp.razon_social}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Period selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="visador-periodo-label">Periodo</InputLabel>
          <Select
            labelId="visador-periodo-label"
            value={periodo}
            label="Periodo"
            onChange={(e) => setPeriodo(e.target.value)}
          >
            {periodos.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Refresh */}
        <IconButton
          onClick={cargarPendientes}
          disabled={loading || !rutEmpresa}
          color="primary"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
          title="Actualizar lista"
        >
          {loading ? <CircularProgress size={20} /> : <Refresh />}
        </IconButton>
      </Box>

      {/* Badge count */}
      {!loading && rutEmpresa && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={
              movimientos.length === 0
                ? 'Sin pendientes'
                : `${movimientos.length} pendiente${movimientos.length !== 1 ? 's' : ''}`
            }
            color={movimientos.length === 0 ? 'success' : 'warning'}
            size="small"
            variant="outlined"
          />
        </Box>
      )}

      {/* No empresa selected */}
      {!rutEmpresa && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <AssignmentTurnedIn sx={{ fontSize: 56, opacity: 0.3, mb: 2 }} />
          <Typography variant="body1">Selecciona una empresa para ver los documentos</Typography>
        </Box>
      )}

      {/* Error */}
      {errorCarga && rutEmpresa && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorCarga('')}>
          {errorCarga}
        </Alert>
      )}

      {/* Loading skeletons */}
      {loading && (
        <Box>
          {[1, 2, 3, 4].map((n) => (
            <MovimientoCardSkeleton key={n} />
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!loading && rutEmpresa && movimientos.length === 0 && !errorCarga && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <CheckCircleOutline
            sx={{ fontSize: 64, color: 'success.light', mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            No hay documentos pendientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Todos los documentos del periodo han sido asociados.
          </Typography>
        </Box>
      )}

      {/* Movement cards */}
      {!loading && movimientos.length > 0 && (
        <Box>
          {movimientos.map((mov) => (
            <MovimientoCard
              key={`${mov.rut_proveedor}-${mov.folio}`}
              mov={mov}
              onClick={handleCardClick}
              tieneAdjunto={adjuntosIds.has(mov.id)}
            />
          ))}
        </Box>
      )}

      {/* Association dialog */}
      <AsociarDialog
        open={dialogOpen}
        movimiento={movimientoActivo}
        rutEmpresa={rutEmpresa}
        periodo={periodo}
        cuentas={cuentas}
        centros={centros}
        clasificadores={clasificadores}
        onClose={handleDialogClose}
        onGuardado={handleGuardado}
      />

      {/* Success snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VisadorPage;
